/**
 * AdminMenuService
 *
 * Provides product data loading functionality for the admin portal.
 * Reuses MenuService but adds admin-specific helpers for filtering and searching.
 */

import { MenuProduct, MenuService } from './MenuService.ts';
import Fuse from 'fuse.js';

/**
 * Load products for a specific store (by retailer ID)
 */
export async function loadStoreProducts(retailerId: string): Promise<MenuProduct[]> {
  try {
    const response = await MenuService.getMenuProducts(retailerId);
    return response.products;
  } catch (error) {
    throw new Error(`Failed to load products: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Extract unique categories from products
 */
export function extractCategories(products: MenuProduct[]): string[] {
  const categories = new Set<string>();

  products.forEach(product => {
    if (product.category) {
      // Normalize category names (lowercase for consistency)
      categories.add(product.category);
    }
  });

  // Convert to array and sort alphabetically
  return Array.from(categories).sort((a, b) => a.localeCompare(b));
}

/**
 * Extract unique brands from products
 */
export function extractBrands(products: MenuProduct[]): string[] {
  const brands = new Set<string>();

  products.forEach(product => {
    if (product.brand?.name) {
      brands.add(product.brand.name);
    }
  });

  // Convert to array and sort alphabetically
  return Array.from(brands).sort((a, b) => a.localeCompare(b));
}

/**
 * Extract unique brands from products filtered by categories
 */
export function extractBrandsByCategories(products: MenuProduct[], categories?: string[]): string[] {
  if (!categories || categories.length === 0) {
    return extractBrands(products);
  }

  const filteredProducts = products.filter(p => p.category && categories.includes(p.category));
  return extractBrands(filteredProducts);
}

/**
 * Extract unique subcategories from products
 */
export function extractSubcategories(products: MenuProduct[]): string[] {
  const subcategories = new Set<string>();

  products.forEach(product => {
    if (product.subcategory) {
      subcategories.add(product.subcategory);
    }
  });

  // Convert to array and sort alphabetically
  return Array.from(subcategories).sort((a, b) => a.localeCompare(b));
}

/**
 * Extract unique subcategories from products filtered by categories
 */
export function extractSubcategoriesByCategory(products: MenuProduct[], categories?: string[]): string[] {
  if (!categories || categories.length === 0) {
    return extractSubcategories(products);
  }

  const filteredProducts = products.filter(p => p.category && categories.includes(p.category));
  return extractSubcategories(filteredProducts);
}

/**
 * Search products by name or brand using fuzzy matching
 */
export function searchProducts(query: string, products: MenuProduct[]): MenuProduct[] {
  if (!query || query.trim() === '') {
    return [];
  }

  // Configure Fuse.js for fuzzy search across multiple metadata fields
  const fuse = new Fuse(products, {
    keys: [
      { name: 'name', weight: 3 },
      { name: 'brand.name', weight: 2 },
      { name: 'category', weight: 1.5 },
      { name: 'subcategory', weight: 1 },
      { name: 'strainType', weight: 1 },
      { name: 'tags', weight: 0.8 },
    ],
    threshold: 0.35,
    includeScore: true,
    ignoreLocation: true,
  });

  // Perform search
  const results = fuse.search(query);

  // Return top 20 results
  return results.slice(0, 20).map(result => result.item);
}

/**
 * Get product by ID
 */
export function getProductById(productId: string, products: MenuProduct[]): MenuProduct | undefined {
  return products.find(p => p.id === productId);
}

/**
 * Format product for display (name + brand)
 */
export function formatProductDisplay(product: MenuProduct): string {
  const brandName = product.brand?.name || 'Unknown Brand';
  return `${product.name} - ${brandName}`;
}

/**
 * Get available weight options for a product
 */
export function getProductWeights(product: MenuProduct): string[] {
  if (!product.variants || product.variants.length === 0) {
    return [];
  }

  return product.variants
    .map(v => v.option)
    .filter(option => option && option.trim() !== '')
    .sort((a, b) => {
      // Try to sort numerically if they're weight values
      const aNum = parseFloat(a);
      const bNum = parseFloat(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return a.localeCompare(b);
    });
}

/**
 * Format weight value with leading zeros and normalize format (e.g., ".5g" -> "0.5g", "1/8OZ" -> "1/8")
 * Matches the formatWeight function used in DutchieMenu.tsx
 */
function formatWeight(weight: string): string {
  if (!weight || weight === 'all') return weight;

  // Trim whitespace and convert to lowercase for consistent comparison
  let normalized = weight.trim().toLowerCase();

  // If the weight starts with a decimal point, add leading zero
  if (normalized.startsWith('.')) {
    normalized = '0' + normalized;
  }

  // Normalize "oz" suffix for fractional ounces (remove spaces and "oz")
  // This ensures "1/8oz", "1/8 oz", and "1/8" all match
  normalized = normalized.replace(/\s*oz$/i, '');

  return normalized;
}

/**
 * Check if a weight value is valid (not empty, not 'n/a')
 */
function isValidWeight(weight: string): boolean {
  if (!weight || weight === 'all') return true;
  const normalized = weight.toLowerCase().trim();
  return normalized !== 'n/a' && normalized !== '';
}

/**
 * Extract unique weights from products, optionally filtered by categories and brands
 */
export function extractWeights(
  products: MenuProduct[],
  categories?: string[],
  brands?: string[]
): string[] {
  // Filter products by categories and brands if specified
  let filteredProducts = products;

  if (categories && categories.length > 0) {
    filteredProducts = filteredProducts.filter(p => p.category && categories.includes(p.category));
  }

  if (brands && brands.length > 0) {
    filteredProducts = filteredProducts.filter(p =>
      p.brand?.name && brands.includes(p.brand.name)
    );
  }

  // Extract all weights from all variants
  const weights = new Set<string>();

  filteredProducts.forEach(product => {
    if (product.variants && product.variants.length > 0) {
      product.variants.forEach(variant => {
        if (variant.option && isValidWeight(variant.option)) {
          weights.add(formatWeight(variant.option));
        }
      });
    }
  });

  // Convert to array and sort alphabetically
  return Array.from(weights).sort((a, b) => a.localeCompare(b));
}

export default {
  loadStoreProducts,
  extractCategories,
  extractBrands,
  extractBrandsByCategories,
  extractSubcategories,
  extractSubcategoriesByCategory,
  extractWeights,
  searchProducts,
  getProductById,
  formatProductDisplay,
  getProductWeights
};
